import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

import type { BillingResponse } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/ErrorState';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardSkeleton } from '@/components/ui/Skeletons';
import {
  STANDING_COLOUR,
  STATUS_COLOUR,
  STATUS_LABELS,
  STATUS_MEANING,
  accountStanding,
  formatDate,
  isWritable,
} from '@/lib/organization';

import { useBilling } from './useBilling';

/** Under the title, while loading and once loaded alike. */
const DESCRIPTION = 'Your organization’s standing with Activist Technologies.';

/** What the status card says, whichever way it is drawn. */
function StatusSummary({ record }: { record: BillingResponse }) {
  const standing = accountStanding(record);
  const label = STATUS_LABELS[record.status] ?? record.status;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
          Account status
        </Typography>
        {/* The word carries the status; the colour only reinforces it. */}
        <Chip
          size="small"
          label={label}
          color={STATUS_COLOUR[record.status] ?? 'default'}
          data-testid="account-status-chip"
        />
      </Box>

      <Typography variant="body2" data-testid="account-status-message" sx={{ mt: 1 }}>
        {STATUS_MEANING[record.status] ?? `This account is ${label.toLowerCase()}.`}
      </Typography>

      {/* Null until staff first change the status. Left out rather than
          shown as "since" and a blank. */}
      {record.status_changed_at ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="account-status-since-value"
          sx={{ mt: 0.5 }}
        >
          {label} since {formatDate(record.status_changed_at)}.
        </Typography>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Account standing:
        </Typography>
        <Chip
          size="small"
          label={standing}
          color={STANDING_COLOUR[standing]}
          data-testid="account-standing-chip"
        />
      </Box>
    </>
  );
}

/**
 * The organization's account, summed up: its status and what that means, its
 * standing, and the notification allowance.
 *
 * It reads the same record as Billing, under the same key, so moving between
 * the two costs no second request and a saved billing contact refreshes both.
 *
 * For a suspended or deactivated account the status card is an alert, because
 * that is the one thing on the page the Super Admin must not miss: nothing they
 * try elsewhere in the portal will be saved.
 */
export function AccountPage() {
  const query = useBilling();

  if (query.isPending) {
    return (
      <Box data-testid="account-page">
        <PageHeader title="Account" description={DESCRIPTION} />
        {/* The standing and the allowance are cards, not a form, so their
            placeholders are cards too - each as tall as the card it stands in
            for, on a phone and on a desktop. */}
        <Box sx={{ maxWidth: 760, display: 'grid', gap: 3 }}>
          <CardSkeleton
            lines={3}
            label="Loading your account…"
            minHeight={{ xs: 150, md: 130 }}
          />
          <CardSkeleton lines={2} minHeight={{ xs: 158, md: 118 }} />
          <Skeleton variant="rounded" width={260} height={37} />
        </Box>
      </Box>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Box data-testid="account-page" sx={{ maxWidth: 560 }}>
        <PageHeader title="Account" />
        <ErrorState
          title="Your account could not be loaded."
          onRetry={() => void query.refetch()}
          testId="account"
        />
      </Box>
    );
  }

  const record = query.data;
  const limit = record.effective_monthly_notification_limit;
  const blocked = !isWritable(record.status);

  return (
    <Box data-testid="account-page" sx={{ maxWidth: 760 }}>
      <PageHeader title="Account" description={DESCRIPTION} />

      {blocked ? (
        <Alert
          severity={record.status === 'DEACTIVATED' ? 'error' : 'warning'}
          role="alert"
          icon={false}
          data-testid="account-status-card"
          sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}
        >
          <StatusSummary record={record} />
        </Alert>
      ) : (
        <Paper variant="outlined" data-testid="account-status-card" sx={{ p: 2, mb: 3 }}>
          <StatusSummary record={record} />
        </Paper>
      )}

      {typeof limit === 'number' ? (
        <Paper
          variant="outlined"
          data-testid="account-allowance-card"
          sx={{ p: 2, mb: 3 }}
        >
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
            Notification allowance
          </Typography>
          <Typography data-testid="account-allowance-value" sx={{ mt: 1 }}>
            {limit} {limit === 1 ? 'notification' : 'notifications'} a month
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            How many you have sent this month will appear here once notifications are
            available in the portal.
          </Typography>
        </Paper>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          component={RouterLink}
          to={ROUTES.account.billing}
          variant="secondary"
          data-testid="account-billing-link"
        >
          View billing
        </Button>
        <Button
          component={RouterLink}
          to={ROUTES.account.profile}
          variant="secondary"
          data-testid="account-profile-link"
        >
          View profile
        </Button>
      </Box>
    </Box>
  );
}
